import * as Sentry from '@sentry/react'
import config from 'services/config'
import {
  checkIfSessionExists,
  handleUnsuccesfulResponse
} from 'services/helpers/authorizationHandlers'
import LocalUserService from 'services/localUserService'

export async function responseHandler(api) {
  const response = await api()

  if (!response) {
    throw new Error('error during fetch')
  }

  const res = Object.prototype.hasOwnProperty.call(response, 'isSuccess')
    ? response
    : response.data

  const resSuccess = res?.isSuccess ?? res?.IsSuccess
  if (resSuccess === false) {
    throw new Error(res.message ?? res.Message)
  }

  if (resSuccess) {
    return res.result ?? res.Result
  }

  if (res?.message === 'SessionTimedOut') {
    console.warn('SessionTimedOut')
  }
}

const baseUrl = config.apiUrl + '/'

const userService = new LocalUserService()

async function fetchHandler(
  method,
  url = ``,
  body,
  abortController,
  withSessionId = true,
  dataType
) {
  let signal = null

  if (abortController) {
    signal = abortController.signal
  }

  checkIfSessionExists()

  const getHeaders = () => {
    return {
      'Content-Type':
        body instanceof FormData ? 'multipart/form-data' : 'application/json',
      'Cache-Control': 'no-cache',
      ...(withSessionId
        ? { 'sp-auth': userService.getCurrentUserInfo()?.sessionId }
        : null)
    }
  }

  const readData = (dataType, data) => {
    if (dataType === 'text') {
      return data.text()
    } else if (dataType === 'blob') {
      return data.blob()
    } else if (dataType === 'json') {
      return data.json()
    } else {
      return data
    }
  }

  return fetch(baseUrl + url, {
    method: method,
    headers: getHeaders(),
    body: body instanceof FormData ? body : body ? JSON.stringify(body) : null,
    signal
  })
    .then(res => readData(dataType, res))
    .then(data => {
      if (dataType !== 'json') {
        return data
      }
      const success = data?.isSuccess ?? data?.IsSuccess
      const payload = data?.result ?? data?.Result
      if (success) {
        return payload
      } else if (success === false) {
        // Fail-safe: do not run global handler for logs endpoint failure (avoids recursion / loop)
        const isLogsEndpoint = url === 'logs' || url.endsWith('/logs')
        if (isLogsEndpoint) {
          throw new Error(data?.message || data?.Message || 'Logs request failed')
        }
        handleUnsuccesfulResponse(data, url)
      }

      // LLM Platform endpoints return data directly without isSuccess wrapper
      if (url?.includes('llm-platform/')) {
        if (data && typeof data === 'object' && !data.message && !data.error && !data.detail) {
          return data
        }
        if (data?.error || data?.detail) {
          const errorMessage = data?.detail || data?.error || 'Unknown error'
          const userMessage = data?.message || 'LLM Platform service error'
          throw new Error(`${userMessage}: ${errorMessage}`)
        }
      }
    })
    .catch(error => {
      if (!error.toString().includes('The user aborted a request.')) {
        Sentry.captureException(error)
      }

      throw error
    })
}

export const fetcher = {
  get: (
    url,
    queryParams = {},
    abortController,
    isSessionExist = true,
    dataType = 'json'
  ) => {
    const cleanParams = JSON.parse(JSON.stringify(queryParams))

    return fetchHandler(
      'GET',
      url +
        (Object.keys(cleanParams).length === 0
          ? ''
          : `?${new URLSearchParams(cleanParams)}`),
      null,
      abortController,
      isSessionExist,
      dataType
    )
  },
  post: (url, body, abortController, isSessionExist = true, dataType = 'json') =>
    fetchHandler('POST', url, body, abortController, isSessionExist, dataType),
  put: (url, body, abortController, isSessionExist = true, dataType = 'json') =>
    fetchHandler('PUT', url, body, abortController, isSessionExist, dataType),
  del: (url, body, abortController, isSessionExist = true, dataType = 'json') =>
    fetchHandler('DELETE', url, body, abortController, isSessionExist, dataType),
  patch: (url, body, abortController, isSessionExist = true, dataType = 'json') =>
    fetchHandler('PATCH', url, body, abortController, isSessionExist, dataType)
}
